import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Mail, Mic2, Users, LogOut, Loader2, Lock, Inbox, RefreshCw, Reply, CheckCircle2, CircleDot, Send, Link2, Megaphone, CalendarCheck, BookOpen, FileText, FolderKanban,
} from "lucide-react";
import { FadeUp, Page, Meta } from "../components/Reveal";
import { Field, inputCls, Toggle } from "../components/forms";
import { ArticlesPanel } from "../components/admin/ArticlesPanel";
import { ProjectsPanel } from "../components/admin/ProjectsPanel";
import { ContentPanel } from "../components/admin/ContentPanel";
import {
  adminLogin, adminMe, adminGetMessages, adminGetConferences, adminGetSubscribers,
  adminSetContactStatus, adminSetConferenceStatus, adminSendNewsletter, adminGetCampaigns,
  getLinks, adminSetLinks, getAnnouncement, adminSetAnnouncement, getAvailability, adminSetAvailability,
  adminGetArticles, getContent, adminGetProjects,
} from "../lib/api";

const TOKEN_KEY = "el_admin_token";

const TABS = [
  { key: "messages", label: "Messages", Icon: Mail, id: "admin-tab-messages" },
  { key: "conferences", label: "Invitations", Icon: Mic2, id: "admin-tab-conferences" },
  { key: "abonnes", label: "Abonnés", Icon: Users, id: "admin-tab-abonnes" },
  { key: "articles", label: "Articles", Icon: BookOpen, id: "admin-tab-articles" },
  { key: "projets", label: "Projets", Icon: FolderKanban, id: "admin-tab-projets" },
  { key: "contenus", label: "Contenus", Icon: FileText, id: "admin-tab-contenus" },
  { key: "infolettre", label: "Infolettre", Icon: Send, id: "admin-tab-infolettre" },
  { key: "liens", label: "Liens", Icon: Link2, id: "admin-tab-liens" },
  { key: "annonce", label: "Annonce", Icon: Megaphone, id: "admin-tab-annonce" },
  { key: "dispo", label: "Disponibilité", Icon: CalendarCheck, id: "admin-tab-dispo" },
];

const FILTERS = [
  { key: "tous", label: "Tous", id: "admin-filter-tous" },
  { key: "nouveau", label: "À traiter", id: "admin-filter-a-traiter" },
  { key: "traite", label: "Traités", id: "admin-filter-traites" },
];

const LINK_FIELDS = [
  { key: "instagram", label: "Instagram", placeholder: "https://instagram.com/…" },
  { key: "linkedin", label: "LinkedIn", placeholder: "https://linkedin.com/in/…" },
  { key: "facebook", label: "Facebook", placeholder: "https://facebook.com/…" },
  { key: "tiktok", label: "TikTok", placeholder: "https://tiktok.com/@…" },
  { key: "youtube", label: "YouTube", placeholder: "https://youtube.com/@…" },
  { key: "sexualis", label: "Sexualis (site)", placeholder: "https://…" },
  { key: "fesses", label: "Fesses & Tendresse (site)", placeholder: "https://…" },
  { key: "doigte", label: "Avec doigté ! (plateforme d'écoute)", placeholder: "https://open.spotify.com/show/…" },
  { key: "rouge_video_1", label: "102,7 Rouge : capsule vidéo 1 (YouTube ou .mp4)", placeholder: "https://youtube.com/watch?v=…" },
  { key: "rouge_video_2", label: "102,7 Rouge : capsule vidéo 2 (YouTube ou .mp4)", placeholder: "https://youtube.com/watch?v=…" },
  { key: "rouge_video_3", label: "102,7 Rouge : capsule vidéo 3 (YouTube ou .mp4)", placeholder: "https://youtube.com/watch?v=…" },
  { key: "rouge_site", label: "102,7 Rouge : site web", placeholder: "https://www.rougefm.ca/estrie/" },
];

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("fr-CA", {
      year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function mailtoFor(item, isConference) {
  const subject = isConference
    ? `Re : Votre invitation (${item.organization})`
    : `Re : ${item.subject || "Votre message à Eugénie"}`;
  const body = `Bonjour ${item.name},\n\nMerci pour votre message. \n\n\n\n—\nEugénie Larrivée\nSexologue & Psychothérapeute · Entrepreneure à impact social · Vulgarisatrice`;
  return `mailto:${item.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function LoginForm({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const data = await adminLogin({ email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      toast.success(`Bonjour ${data.name || "Eugénie"}. Bienvenue dans votre espace.`);
      onSuccess(data.token);
    } catch (err) {
      setError(err.message || "Connexion impossible");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-md flex-col justify-center px-6 pt-32 pb-24">
      <FadeUp>
        <div className="rounded-[1.75rem] border border-linen bg-shell p-10">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-clay/10">
            <Lock className="h-5 w-5 text-clay" strokeWidth={1.5} />
          </div>
          <h1 className="mt-6 font-serif text-3xl font-light tracking-tight">
            Espace <em className="text-clay">admin.</em>
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-smoke">
            Un espace protégé pour lire vos messages, invitations et abonnés.
          </p>
          <form data-testid="admin-login-form" onSubmit={onSubmit} className="mt-8 space-y-7">
            <Field label="Courriel" htmlFor="admin-email">
              <input id="admin-email" data-testid="admin-email-input" required type="email"
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="contact@eugenielarrivee.com" className={inputCls} />
            </Field>
            <Field label="Mot de passe" htmlFor="admin-password">
              <input id="admin-password" data-testid="admin-password-input" required type="password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••" className={inputCls} />
            </Field>
            {error && (
              <p data-testid="admin-login-error" className="text-sm text-clay">{error}</p>
            )}
            <button type="submit" data-testid="admin-login-submit" disabled={loading}
              className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-clay px-8 py-4 text-xs font-medium uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:bg-charcoal disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Se connecter"}
            </button>
          </form>
        </div>
      </FadeUp>
    </div>
  );
}

function MessageCard({ item, testId, isConference, onToggle, toggling }) {
  const status = item.status || "nouveau";
  const done = status === "traite";
  return (
    <div data-testid={testId} className={`rounded-[1.75rem] border p-7 transition-colors duration-300 ${
      done ? "border-linen bg-shell/50" : "border-linen bg-shell"
    }`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <p className="font-serif text-xl font-light tracking-tight">{item.name}</p>
          <span data-testid={`${testId}-status`} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-medium uppercase tracking-[0.15em] ${
            done ? "bg-sage/60 text-smoke" : "bg-clay/10 text-clay"
          }`}>
            {done ? <CheckCircle2 className="h-3 w-3" /> : <CircleDot className="h-3 w-3" />}
            {done ? "Traité" : "À traiter"}
          </span>
        </div>
        <p className="text-xs text-ash">{formatDate(item.created_at)}</p>
      </div>
      <a href={`mailto:${item.email}`} className="mt-1 inline-block text-sm text-clay hover:underline">{item.email}</a>
      {isConference && (
        <p className="mt-2 text-xs font-medium uppercase tracking-[0.15em] text-ash">
          {item.organization} · {item.event_type}
          {item.preferred_date ? ` · ${item.preferred_date}` : ""}
        </p>
      )}
      {!isConference && item.subject && (
        <p className="mt-3 text-xs font-medium uppercase tracking-[0.15em] text-clay">{item.subject}</p>
      )}
      <p className="mt-3 text-sm leading-relaxed text-smoke">{item.message}</p>
      <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-linen pt-5">
        <a href={mailtoFor(item, isConference)} data-testid={`${testId}-reply`}
          className="group inline-flex items-center gap-2 rounded-full bg-clay px-5 py-2.5 text-xs font-medium uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:bg-charcoal">
          <Reply className="h-3.5 w-3.5" />
          Répondre
        </a>
        <button onClick={() => onToggle(item)} disabled={toggling} data-testid={`${testId}-toggle`}
          className="inline-flex items-center gap-2 rounded-full border border-linen px-5 py-2.5 text-xs font-medium uppercase tracking-[0.15em] text-smoke transition-colors duration-300 hover:border-clay hover:text-clay disabled:opacity-60">
          {toggling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : done ? <CircleDot className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
          {done ? "Remettre à traiter" : "Marquer traité"}
        </button>
      </div>
    </div>
  );
}

function NewsletterPanel({ token, subscriberCount, campaigns, onSent }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [idemKey, setIdemKey] = useState(() => crypto.randomUUID());
  const [sending, setSending] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!window.confirm(`Envoyer « ${subject} » à ${subscriberCount} abonné(s) ?`)) return;
    setSending(true);
    try {
      const campaign = await adminSendNewsletter(token, { subject, body, idempotency_key: idemKey });
      toast.success(`Infolettre envoyée : ${campaign.sent} réussie(s), ${campaign.failed} en échec.`);
      setSubject("");
      setBody("");
      setIdemKey(crypto.randomUUID());
      onSent();
    } catch (err) {
      toast.error(err.message === "request_failed" ? "Envoi impossible. Réessayez." : err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid gap-10 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <form data-testid="newsletter-compose-form" onSubmit={onSubmit}
          className="rounded-[1.75rem] border border-linen bg-shell p-8 md:p-10">
          <h2 className="font-serif text-2xl font-light tracking-tight md:text-3xl">
            Écrire <em className="text-clay">« Entre nous »</em>
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-smoke">
            Envoyée à <strong>{subscriberCount}</strong> abonné(s) · Expéditrice : Eugénie Larrivée ·
            Les réponses arrivent à contact@eugenielarrivee.com
          </p>
          <div className="mt-8 space-y-7">
            <Field label="Objet du courriel *" htmlFor="nl-subject">
              <input id="nl-subject" data-testid="newsletter-subject-input" required minLength={3} maxLength={160}
                value={subject} onChange={(e) => setSubject(e.target.value)}
                placeholder="Ex. : Entre nous, édition d'octobre" className={inputCls} />
            </Field>
            <Field label="Votre réflexion du mois *" htmlFor="nl-body">
              <textarea id="nl-body" data-testid="newsletter-body-input" required minLength={20} rows={9}
                value={body} onChange={(e) => setBody(e.target.value)}
                placeholder={"Bonjour,\n\nCe mois-ci, je vous partage une réflexion qui m'habite…\n\n(Séparez vos paragraphes par une ligne vide.)"}
                className={`${inputCls} resize-none`} />
            </Field>
            <button type="submit" data-testid="newsletter-send-button" disabled={sending || subscriberCount === 0}
              className="group inline-flex items-center gap-2 rounded-full bg-clay px-8 py-4 text-xs font-medium uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:bg-charcoal disabled:opacity-60">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {sending ? "Envoi en cours" : "Envoyer l'infolettre"}
            </button>
          </div>
        </form>
      </div>
      <div className="lg:col-span-5">
        <h3 className="text-xs font-medium uppercase tracking-[0.25em] text-ash">Envois passés</h3>
        <div className="mt-5 space-y-4">
          {campaigns.length === 0 ? (
            <p className="rounded-[1.75rem] border border-dashed border-linen py-10 text-center font-serif text-xl font-light italic text-smoke">
              Aucune infolettre envoyée pour l'instant.
            </p>
          ) : campaigns.map((c, i) => (
            <div key={c.id} data-testid={`campaign-${i + 1}`} className="rounded-[1.75rem] border border-linen bg-shell p-6">
              <p className="font-serif text-lg font-light leading-snug tracking-tight">{c.subject}</p>
              <p className="mt-2 text-xs text-ash">
                {formatDate(c.created_at)} · {c.sent} envoyée(s){c.failed > 0 ? ` · ${c.failed} en échec` : ""}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function LinksPanel({ token, links, onSaved }) {
  const [form, setForm] = useState(links);
  const [saving, setSaving] = useState(false);

  useEffect(() => setForm(links), [links]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminSetLinks(token, form);
      toast.success("Liens enregistrés. Ils sont à jour partout sur le site.");
      onSaved();
    } catch {
      toast.error("Impossible d'enregistrer les liens.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form data-testid="links-form" onSubmit={onSubmit}
      className="max-w-2xl rounded-[1.75rem] border border-linen bg-shell p-8 md:p-10">
      <h2 className="font-serif text-2xl font-light tracking-tight md:text-3xl">
        Liens du <em className="text-clay">site.</em>
      </h2>
      <p className="mt-3 text-sm leading-relaxed text-smoke">
        Ces adresses alimentent la navigation, le pied de page, la page Contact et la page Projets.
        Laissez vide pour masquer un réseau.
      </p>
      <div className="mt-8 space-y-7">
        {LINK_FIELDS.map((f) => (
          <Field key={f.key} label={f.label} htmlFor={`link-${f.key}`}>
            <input id={`link-${f.key}`} data-testid={`link-input-${f.key}`} type="text"
              value={form[f.key] || ""} onChange={(e) => setForm((v) => ({ ...v, [f.key]: e.target.value }))}
              placeholder={f.placeholder} className={inputCls} />
          </Field>
        ))}
        <button type="submit" data-testid="links-save-button" disabled={saving}
          className="group inline-flex items-center gap-2 rounded-full bg-clay px-8 py-4 text-xs font-medium uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:bg-charcoal disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
          {saving ? "Enregistrement" : "Enregistrer les liens"}
        </button>
      </div>
    </form>
  );
}

function AnnouncementPanel({ token, announcement, onSaved }) {
  const [form, setForm] = useState(announcement);
  const [saving, setSaving] = useState(false);

  useEffect(() => setForm(announcement), [announcement]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminSetAnnouncement(token, form);
      toast.success(form.enabled ? "Annonce activée sur le site." : "Annonce enregistrée (masquée).");
      onSaved();
    } catch {
      toast.error("Impossible d'enregistrer l'annonce.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form data-testid="announcement-form" onSubmit={onSubmit}
      className="max-w-2xl rounded-[1.75rem] border border-linen bg-shell p-8 md:p-10">
      <div className="flex items-start justify-between gap-6">
        <div>
          <h2 className="font-serif text-2xl font-light tracking-tight md:text-3xl">
            Message <em className="text-clay">pop-up.</em>
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-smoke">
            Une carte discrète apparaît en bas du site quelques secondes après l'arrivée du visiteur.
            Idéale pour annoncer une pause clinique, un lancement ou une nouvelle importante.
          </p>
        </div>
        <Toggle checked={!!form.enabled} onChange={(v) => setForm((f) => ({ ...f, enabled: v }))} testId="announcement-toggle" />
      </div>
      <p className="mt-2 text-xs uppercase tracking-[0.2em] text-ash" data-testid="announcement-state">
        {form.enabled ? "Visible sur le site" : "Masquée"}
      </p>
      <div className="mt-8 space-y-7">
        <Field label="Titre (petite signature)" htmlFor="ann-title">
          <input id="ann-title" data-testid="announcement-title-input" type="text" maxLength={120}
            value={form.title || ""} onChange={set("title")}
            placeholder="Un petit mot d'Eugénie" className={inputCls} />
        </Field>
        <Field label="Message *" htmlFor="ann-message">
          <textarea id="ann-message" data-testid="announcement-message-input" required minLength={10} maxLength={1200} rows={5}
            value={form.message || ""} onChange={set("message")}
            placeholder="Je suis présentement en congé de maternité…" className={`${inputCls} resize-none`} />
        </Field>
        <div className="grid gap-7 md:grid-cols-2">
          <Field label="Texte du bouton (optionnel)" htmlFor="ann-cta-label">
            <input id="ann-cta-label" data-testid="announcement-cta-label-input" type="text" maxLength={80}
              value={form.cta_label || ""} onChange={set("cta_label")}
              placeholder="Découvrir Sexualis" className={inputCls} />
          </Field>
          <Field label="Lien du bouton (optionnel)" htmlFor="ann-cta-url">
            <input id="ann-cta-url" data-testid="announcement-cta-url-input" type="text" maxLength={300}
              value={form.cta_url || ""} onChange={set("cta_url")}
              placeholder="https://…" className={inputCls} />
          </Field>
        </div>
        <p className="text-xs italic text-ash">
          Les visiteurs peuvent fermer le message ; il réapparaît si vous modifiez son texte.
        </p>
        <button type="submit" data-testid="announcement-save-button" disabled={saving}
          className="group inline-flex items-center gap-2 rounded-full bg-clay px-8 py-4 text-xs font-medium uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:bg-charcoal disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Megaphone className="h-4 w-4" />}
          {saving ? "Enregistrement" : "Enregistrer l'annonce"}
        </button>
      </div>
    </form>
  );
}

function AvailabilityPanel({ token, availability, onSaved }) {
  const [form, setForm] = useState(availability);
  const [saving, setSaving] = useState(false);

  useEffect(() => setForm(availability), [availability]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminSetAvailability(token, form);
      toast.success(form.open ? "Disponibilités : pratique ouverte." : "Disponibilités : pratique en pause.");
      onSaved();
    } catch {
      toast.error("Impossible d'enregistrer les disponibilités.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form data-testid="availability-form" onSubmit={onSubmit}
      className="max-w-2xl rounded-[1.75rem] border border-linen bg-shell p-8 md:p-10">
      <div className="flex items-start justify-between gap-6">
        <div>
          <h2 className="font-serif text-2xl font-light tracking-tight md:text-3xl">
            Disponibilités <em className="text-clay">cliniques.</em>
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-smoke">
            Ce réglage met à jour la page Clinique : la mention sous le hero et le module
            « Disponibilités » en bas de page. Le reste de la page ne change jamais.
          </p>
        </div>
        <Toggle checked={!!form.open} onChange={(v) => setForm((f) => ({ ...f, open: v }))} testId="availability-toggle" />
      </div>
      <p className="mt-2 text-xs uppercase tracking-[0.2em] text-ash" data-testid="availability-state">
        {form.open ? "Ouverte : j'accueille de nouvelles demandes" : "En pause : renvoi vers Sexualis"}
      </p>
      <div className="mt-8 space-y-7">
        <Field label="Note personnalisée (optionnel)" htmlFor="dispo-note">
          <textarea id="dispo-note" data-testid="availability-note-input" rows={3} maxLength={300}
            value={form.note || ""} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            placeholder="Ex. : Retour prévu au printemps 2027 ; les demandes seront traitées à ce moment."
            className={`${inputCls} resize-none`} />
        </Field>
        <button type="submit" data-testid="availability-save-button" disabled={saving}
          className="group inline-flex items-center gap-2 rounded-full bg-clay px-8 py-4 text-xs font-medium uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:bg-charcoal disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarCheck className="h-4 w-4" />}
          {saving ? "Enregistrement" : "Enregistrer"}
        </button>
      </div>
    </form>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [checking, setChecking] = useState(!!token);
  const [tab, setTab] = useState("messages");
  const [filter, setFilter] = useState("tous");
  const [data, setData] = useState({ messages: [], conferences: [], abonnes: [], campaigns: [], links: {}, announcement: {}, availability: {}, articles: [], content: {}, projects: [] });
  const [loadingData, setLoadingData] = useState(false);
  const [togglingId, setTogglingId] = useState("");

  const loadAll = async (t) => {
    setLoadingData(true);
    try {
      const [messages, conferences, abonnes, campaigns, links, announcement, availability, articles, content, projects] = await Promise.all([
        adminGetMessages(t), adminGetConferences(t), adminGetSubscribers(t), adminGetCampaigns(t),
        getLinks(), getAnnouncement(), getAvailability(), adminGetArticles(t), getContent(), adminGetProjects(t),
      ]);
      setData({ messages, conferences, abonnes, campaigns, links, announcement, availability, articles, content, projects });
    } catch {
      toast.error("Impossible de charger les données.");
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    adminMe(token)
      .then(() => loadAll(token))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        toast.error("Session expirée. Reconnectez-vous.");
      })
      .finally(() => setChecking(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    toast.success("À bientôt, Eugénie.");
  };

  const toggleStatus = async (item) => {
    const next = (item.status || "nouveau") === "traite" ? "nouveau" : "traite";
    setTogglingId(item.id);
    try {
      const updated = tab === "conferences"
        ? await adminSetConferenceStatus(token, item.id, next)
        : await adminSetContactStatus(token, item.id, next);
      setData((d) => ({
        ...d,
        [tab]: d[tab].map((x) => (x.id === item.id ? updated : x)),
      }));
      toast.success(next === "traite" ? "Marqué comme traité." : "Remis à traiter.");
    } catch {
      toast.error("Impossible de changer le statut.");
    } finally {
      setTogglingId("");
    }
  };

  if (!token) return <Page testId="admin-page"><LoginForm onSuccess={setToken} /></Page>;
  if (checking) {
    return (
      <Page testId="admin-page">
        <div className="flex min-h-screen items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-clay" />
        </div>
      </Page>
    );
  }

  const linksCount = Object.values(data.links).filter(Boolean).length;
  const lists = {
    messages: data.messages, conferences: data.conferences, abonnes: data.abonnes,
    infolettre: data.campaigns, liens: new Array(linksCount).fill(0),
    articles: data.articles, projets: data.projects,
    contenus: new Array(Object.keys(data.content || {}).length).fill(0),
    annonce: [], dispo: [],
  };
  const pendingMessages = data.messages.filter((m) => (m.status || "nouveau") !== "traite").length;
  const pendingConferences = data.conferences.filter((m) => (m.status || "nouveau") !== "traite").length;
  const pending = { messages: pendingMessages, conferences: pendingConferences };
  const current = ["abonnes", "articles", "projets", "contenus", "infolettre", "liens", "annonce", "dispo"].includes(tab) || filter === "tous"
    ? lists[tab]
    : lists[tab].filter((m) => (m.status || "nouveau") === filter);

  const statValue = (key) => {
    if (key === "annonce") return data.announcement.enabled ? "Active" : "En pause";
    if (key === "dispo") return data.availability.open ? "Ouverte" : "En pause";
    return lists[key].length;
  };

  return (
    <Page testId="admin-page">
      <section className="mx-auto max-w-7xl px-6 pt-32 pb-24 md:px-12 md:pt-40">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <FadeUp><Meta>Espace admin</Meta></FadeUp>
            <FadeUp delay={0.1}>
              <h1 data-testid="admin-dashboard-title" className="mt-4 font-serif text-4xl font-light tracking-tight md:text-5xl">
                Bonjour <em className="text-clay">Eugénie.</em>
              </h1>
            </FadeUp>
          </div>
          <FadeUp delay={0.15} className="flex items-center gap-3">
            <button onClick={() => loadAll(token)} data-testid="admin-refresh-button"
              className="group inline-flex items-center gap-2 rounded-full border border-linen px-5 py-2.5 text-xs font-medium uppercase tracking-[0.15em] text-smoke transition-colors duration-300 hover:border-clay hover:text-clay">
              <RefreshCw className="h-3.5 w-3.5 transition-transform duration-500 group-hover:rotate-180" />
              Actualiser
            </button>
            <button onClick={logout} data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 rounded-full bg-charcoal px-5 py-2.5 text-xs font-medium uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:bg-clay">
              <LogOut className="h-3.5 w-3.5" />
              Quitter
            </button>
          </FadeUp>
        </div>

        <FadeUp delay={0.2}>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {TABS.map((t) => {
              const pendingCount = pending[t.key];
              return (
                <button key={t.key} onClick={() => { setTab(t.key); setFilter("tous"); }} data-testid={`${t.id}-stat`}
                  className={`rounded-[1.75rem] border p-7 text-left transition-colors duration-300 ${
                    tab === t.key ? "border-clay/40 bg-sand/30" : "border-linen bg-shell hover:bg-sand/20"
                  }`}>
                  <t.Icon className="h-6 w-6 text-clay" strokeWidth={1.25} />
                  <p className="mt-4 font-serif text-4xl font-light">{statValue(t.key)}</p>
                  <p className="mt-1 text-xs font-medium uppercase tracking-[0.2em] text-ash">
                    {t.label}
                    {pendingCount > 0 && <span className="ml-2 text-clay">· {pendingCount} à traiter</span>}
                  </p>
                </button>
              );
            })}
          </div>
        </FadeUp>

        <FadeUp delay={0.25}>
          <div data-testid="admin-tabs" className="mt-12 flex flex-wrap items-center gap-3">
            {TABS.map((t) => (
              <button key={t.key} onClick={() => { setTab(t.key); setFilter("tous"); }} data-testid={t.id}
                className={`rounded-full border px-5 py-2 text-xs font-medium uppercase tracking-[0.15em] transition-colors duration-300 ${
                  tab === t.key ? "border-clay bg-clay text-cream" : "border-linen text-smoke hover:border-clay hover:text-clay"
                }`}>
                {t.label}
              </button>
            ))}
            {!["abonnes", "articles", "projets", "contenus", "infolettre", "liens", "annonce", "dispo"].includes(tab) && (
              <div data-testid="admin-status-filters" className="ml-auto flex gap-2">
                {FILTERS.map((f) => (
                  <button key={f.key} onClick={() => setFilter(f.key)} data-testid={f.id}
                    className={`rounded-full px-4 py-2 text-xs font-medium tracking-[0.1em] transition-colors duration-300 ${
                      filter === f.key ? "bg-charcoal text-cream" : "text-ash hover:text-clay"
                    }`}>
                    {f.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </FadeUp>

        <div data-testid="admin-list" className="mt-10">
          {loadingData ? (
            <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-clay" /></div>
          ) : tab === "projets" ? (
            <ProjectsPanel token={token} projects={data.projects} onChanged={() => loadAll(token)} />
          ) : tab === "contenus" ? (
            <ContentPanel token={token} overrides={data.content} onSaved={() => loadAll(token)} />
          ) : tab === "articles" ? (
            <ArticlesPanel token={token} articles={data.articles} onChanged={() => loadAll(token)} />
          ) : tab === "infolettre" ? (
            <NewsletterPanel
              token={token}
              subscriberCount={data.abonnes.length}
              campaigns={data.campaigns}
              onSent={() => loadAll(token)}
            />
          ) : tab === "liens" ? (
            <LinksPanel token={token} links={data.links} onSaved={() => loadAll(token)} />
          ) : tab === "annonce" ? (
            <AnnouncementPanel token={token} announcement={data.announcement} onSaved={() => loadAll(token)} />
          ) : tab === "dispo" ? (
            <AvailabilityPanel token={token} availability={data.availability} onSaved={() => loadAll(token)} />
          ) : current.length === 0 ? (
            <div className="flex flex-col items-center rounded-[1.75rem] border border-dashed border-linen py-20 text-center">
              <Inbox className="h-8 w-8 text-ash" strokeWidth={1.25} />
              <p data-testid="admin-empty-state" className="mt-4 font-serif text-2xl font-light italic text-smoke">
                {filter === "traite" ? "Rien de traité pour l'instant."
                  : filter === "nouveau" ? "Tout est traité. Belle tranquillité."
                  : "Rien pour l'instant. La conversation commence bientôt."}
              </p>
            </div>
          ) : tab === "abonnes" ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {current.map((s, i) => (
                <div key={s.id} data-testid={`admin-subscriber-${i + 1}`}
                  className="flex items-center justify-between gap-4 rounded-[1.75rem] border border-linen bg-shell px-6 py-5">
                  <a href={`mailto:${s.email}`} className="truncate text-sm text-charcoal hover:text-clay">{s.email}</a>
                  <span className="shrink-0 text-xs text-ash">{formatDate(s.created_at).split(",")[0]}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid gap-6 lg:grid-cols-2">
              {current.map((item, i) => (
                <MessageCard key={item.id} item={item} testId={`admin-item-${tab}-${i + 1}`}
                  isConference={tab === "conferences"} onToggle={toggleStatus} toggling={togglingId === item.id} />
              ))}
            </div>
          )}
        </div>
      </section>
    </Page>
  );
}
